import { ThumbsUp, ThumbsDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useVariantVote } from '@/hooks/useVariantVote';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface Props {
  variantId: string;
}

export function VariantVoteBox({ variantId }: Props) {
  const { likes = 0, dislikes = 0, total = 0, myVote = null, setVote, isPending } = useVariantVote(variantId);

  const recommendPct = total > 0 ? Math.round((likes / total) * 100) : null;

  const handle = (vote: 1 | -1) => {
    const next = myVote === vote ? null : vote;
    setVote(next);
    if (next === 1) toast.success('Tak for din anbefaling');
    else if (next === -1) toast.success('Tak for din feedback');
    else toast('Din stemme er fjernet');
  };

  return (
    <div className="bg-card rounded-xl shadow-card p-5 space-y-4">
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="font-display font-semibold text-lg">Hvad mener du om elbilen?</h3>
        {recommendPct !== null && (
          <span className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{recommendPct}%</span> anbefaler
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Button
          type="button"
          variant={myVote === 1 ? 'accent' : 'outline'}
          className={cn('gap-2 h-auto py-3', myVote === 1 && 'ring-2 ring-accent/40')}
          onClick={() => handle(1)}
          disabled={isPending}
          aria-pressed={myVote === 1}
        >
          <ThumbsUp className="w-4 h-4" />
          <span className="flex flex-col items-start leading-tight">
            <span className="font-medium">Anbefal</span>
            <span className="text-xs opacity-80">{likes}</span>
          </span>
        </Button>
        <Button
          type="button"
          variant={myVote === -1 ? 'destructive' : 'outline'}
          className={cn('gap-2 h-auto py-3', myVote === -1 && 'ring-2 ring-destructive/40')}
          onClick={() => handle(-1)}
          disabled={isPending}
          aria-pressed={myVote === -1}
        >
          <ThumbsDown className="w-4 h-4" />
          <span className="flex flex-col items-start leading-tight">
            <span className="font-medium">Frabed</span>
            <span className="text-xs opacity-80">{dislikes}</span>
          </span>
        </Button>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        {total === 0
          ? 'Vær den første til at stemme'
          : `Baseret på ${total} ${total === 1 ? 'stemme' : 'stemmer'}`}
      </p>
    </div>
  );
}
